import { httpsCallable } from 'firebase/functions';
import {
  auth,
  firebaseReady,
  functions,
  getFirebaseDiagnostics,
} from '../firebase/firebase';

export const CALLABLE_ERROR_CODES = Object.freeze({
  AUTH_EMULATOR_UNAVAILABLE: 'auth-emulator-unavailable',
  FUNCTIONS_EMULATOR_UNAVAILABLE: 'functions-emulator-unavailable',
  AUTHENTICATION_TOKEN_MISSING: 'authentication-token-missing',
  EMAIL_UNVERIFIED: 'email-unverified',
  ACCESS_PENDING: 'access-pending',
  APP_CHECK_REQUIRED: 'app-check-required',
  BILLING_NOT_CONFIGURED: 'billing-not-configured',
  ENTITLEMENT_READ_ONLY: 'entitlement-read-only',
});

const serviceError = (code, message, cause = null) => {
  const error = new Error(message, cause ? { cause } : undefined);
  error.code = code;
  return error;
};

export const translateFirebaseError = (error, area = 'functions') => {
  const rawCode = String(error?.code || '');
  const message = String(error?.message || '');
  const diagnostics = getFirebaseDiagnostics();

  if (rawCode.includes('network-request-failed') && area === 'auth') {
    return serviceError(
      CALLABLE_ERROR_CODES.AUTH_EMULATOR_UNAVAILABLE,
      'The local Authentication emulator is unavailable. Start the complete development environment with npm run dev.',
      error
    );
  }
  if (rawCode.includes('unavailable') || rawCode.includes('internal') && /fetch|network/i.test(message)) {
    return serviceError(
      CALLABLE_ERROR_CODES.FUNCTIONS_EMULATOR_UNAVAILABLE,
      diagnostics.emulatorEnabled
        ? 'The local Functions emulator is unavailable. Start the complete development environment with npm run dev.'
        : 'The workspace service is temporarily unavailable. Try again.',
      error
    );
  }
  if (rawCode.includes('unauthenticated')) {
    if (/app.?check/i.test(message)) {
      return serviceError(
        CALLABLE_ERROR_CODES.APP_CHECK_REQUIRED,
        'App Check did not accept this browser. Refresh the page or configure the correct staging App Check key.',
        error
      );
    }
    return serviceError(
      CALLABLE_ERROR_CODES.AUTHENTICATION_TOKEN_MISSING,
      'Your sign-in token was not available to the workspace service. Sign out, sign in again, and retry.',
      error
    );
  }
  if (rawCode.includes('permission-denied') && /approved|pending/i.test(message)) {
    return serviceError(
      CALLABLE_ERROR_CODES.ACCESS_PENDING,
      'Your account is signed in but still awaiting workspace approval.',
      error
    );
  }
  if (rawCode.includes('failed-precondition') && /verify.*email/i.test(message)) {
    return serviceError(
      CALLABLE_ERROR_CODES.EMAIL_UNVERIFIED,
      'Verify your email address before continuing.',
      error
    );
  }
  if (rawCode.includes('failed-precondition') && /Stripe test|price IDs|billing/i.test(message)) {
    return serviceError(
      CALLABLE_ERROR_CODES.BILLING_NOT_CONFIGURED,
      'Stripe sandbox billing is not fully configured. Review the Billing configuration checklist.',
      error
    );
  }
  if (rawCode.includes('failed-precondition') && /entitlement|subscription|real data|pilot/i.test(message)) {
    return serviceError(
      CALLABLE_ERROR_CODES.ENTITLEMENT_READ_ONLY,
      'This workspace is currently read-only for real case data.',
      error
    );
  }
  return error instanceof Error ? error : serviceError('workspace-service-error', message || 'The workspace request failed.');
};

export const callAuthenticatedFunction = async (name, payload = {}) => {
  try {
    await firebaseReady;
    if (!auth.currentUser) {
      throw serviceError(
        CALLABLE_ERROR_CODES.AUTHENTICATION_TOKEN_MISSING,
        'Sign in before using the workspace service.'
      );
    }
    await auth.currentUser.getIdToken(false);
    const callable = httpsCallable(functions, name);
    const result = await callable(payload);
    return result.data;
  } catch (error) {
    if (Object.values(CALLABLE_ERROR_CODES).includes(error?.code)) throw error;
    throw translateFirebaseError(error, 'functions');
  }
};

export default callAuthenticatedFunction;
